import api from './api';
import { ApiResponse } from '../types/api';
import { PaymentType } from '../types/payment';

// Base oficial para Tipos de Pagamento (singular conforme documentação)
const baseUrl = '/v1/tipos-pagamento';

export const paymentTypeService = {
  // Lista tipos de pagamento (GET /v1/tipos-pagamento) com normalização
  async list(): Promise<ApiResponse<PaymentType[]>> {
    const res = await api.get<ApiResponse<any>>(baseUrl);
    const payload = res.data;
    return normalizePaymentTypesResponse(payload);
  },

  // Busca um tipo de pagamento por ID
  async get(id: string): Promise<ApiResponse<PaymentType>> {
    const res = await api.get<ApiResponse<PaymentType>>(`${baseUrl}/${id}`);
    return res.data;
  },

  // Cria um novo tipo de pagamento
  async create(payload: Omit<PaymentType, 'id'>): Promise<ApiResponse<PaymentType>> {
    const body = toCreateUpdatePayload(payload);
    const res = await api.post<ApiResponse<PaymentType>>(baseUrl, body);
    return res.data;
  },

  // Atualiza um tipo de pagamento
  async update(id: string, payload: Partial<Omit<PaymentType, 'id'>>): Promise<ApiResponse<PaymentType>> {
    const body = toCreateUpdatePayload(payload);
    const res = await api.put<ApiResponse<PaymentType>>(`${baseUrl}/${id}`, body);
    return res.data;
  },

  // Exclui um tipo de pagamento
  async delete(id: string): Promise<ApiResponse<null>> {
    const res = await api.delete<ApiResponse<null>>(`${baseUrl}/${id}`);
    return res.data;
  },

  // Ativa / Desativa
  async activate(id: string): Promise<ApiResponse<PaymentType>> {
    const res = await api.post<ApiResponse<PaymentType>>(`${baseUrl}/${id}/ativar`);
    return res.data;
  },

  async deactivate(id: string): Promise<ApiResponse<PaymentType>> {
    const res = await api.post<ApiResponse<PaymentType>>(`${baseUrl}/${id}/desativar`);
    return res.data;
  },
};

export default paymentTypeService;

// Helpers
function normalizePaymentTypesResponse(payload: ApiResponse<any>): ApiResponse<PaymentType[]> {
  const raw = payload?.data;
  let list: any[] = [];

  if (Array.isArray(raw)) {
    list = raw.flatMap((item: any) => (Array.isArray(item) ? item : [item]));
  } else if (Array.isArray((raw as any)?.content)) {
    list = (raw as any).content;
  } else if (raw && typeof raw === 'object') {
    list = [raw];
  } else {
    list = [];
  }

  const items: PaymentType[] = list
    .map(toPaymentType)
    .filter((x): x is PaymentType => x !== null);

  return { ...payload, data: items };
}

function toPaymentType(obj: any): PaymentType | null {
  if (!obj) return null;
  if (typeof obj === 'string') {
    const nome = obj.trim();
    if (!nome) return null;
    return { id: slugify(nome), nome };
  }

  const id = obj.id ?? obj.uuid ?? obj.tipoPagamentoId ?? obj.codigo ?? null;
  const nome = obj.nome ?? obj.tipo ?? obj.label ?? obj.titulo ?? null;
  const descricao = obj.descricao ?? obj.description ?? undefined;
  const ativo = obj.ativo ?? obj.enabled ?? obj.habilitado ?? undefined;
  const permiteParcelamento = obj.permiteParcelamento ?? obj.permite_parcelamento ?? obj.parcelado ?? undefined;
  const maxParcelas = obj.maxParcelas ?? obj.max_parcelas ?? obj.parcelasMax ?? undefined;
  const observacoes = obj.observacoes ?? obj.obs ?? obj.notas ?? obj.notes ?? undefined;
  const ordem = obj.ordem ?? obj.order ?? obj.sequencia ?? obj.posicao ?? undefined;
  const pagamentoImediato = obj.pagamentoImediato ?? obj.imediato ?? obj.pagaNaHora ?? obj.paga_imediato ?? undefined;

  if (!nome) return null;
  return { id: id ?? slugify(String(nome)), nome: String(nome), descricao, ativo, permiteParcelamento, maxParcelas, observacoes, ordem, pagamentoImediato };
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Monta o payload exatamente com os campos esperados pela API
function toCreateUpdatePayload(payload: Partial<Omit<PaymentType, 'id'>>): any {
  const permite = !!payload.permiteParcelamento;
  const max = Number(payload.maxParcelas ?? 0);
  const ordem = payload.ordem;
  return {
    nome: payload.nome?.trim(),
    descricao: payload.descricao?.trim(),
    ativo: payload.ativo,
    permiteParcelamento: permite,
    maxParcelas: permite ? Math.max(1, Math.floor(isNaN(max) ? 0 : max)) : 0,
    observacoes: payload.observacoes?.trim(),
    ordem: typeof ordem === 'number' ? ordem : ordem !== undefined ? Number(ordem) : undefined,
    pagamentoImediato: !!payload.pagamentoImediato,
  };
}